import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import { useBusiness } from "./BusinessContext";

// ── Types ─────────────────────────────────────────────────────────

export type NotifCategory = "invoice" | "error" | "report" | "team";

export interface AppNotification {
  id: string;
  category: NotifCategory;
  title: string;
  body: string;
  timestamp: string; // ISO string
  read: boolean;
  // invoice
  invoiceId?: string;
  supplier?: string;
  amount?: number | null;
  // error
  errorCount?: number;
  errorDate?: string; // YYYY-MM-DD
  // report
  reportType?: "daily" | "weekly" | "monthly" | "custom";
  // team
  requestId?: string;
  requesterName?: string;
  requesterEmail?: string;
}

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
  /** Manually push a notification — useful for report-ready events */
  addNotification: (n: Omit<AppNotification, "id" | "timestamp" | "read">) => void;
  /**
   * Poll for invoice completion after an upload. Pass the ISO timestamp recorded
   * just before the upload call and the number of files submitted. Fires
   * notifications as each invoice transitions out of processing, then stops.
   * This is the reliable path when Supabase realtime isn't configured for invoices.
   */
  watchForInvoices: (since: string, expectedCount: number) => void;
}

// ── Context ───────────────────────────────────────────────────────

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markRead: () => {},
  markAllRead: () => {},
  dismiss: () => {},
  clearAll: () => {},
  addNotification: () => {},
  watchForInvoices: () => {},
});

// ── Persistence ───────────────────────────────────────────────────

const STORAGE_PREFIX = "iscan_notifs_v1_";

function storageKey(businessId: string) {
  return `${STORAGE_PREFIX}${businessId}`;
}

function load(businessId: string): AppNotification[] {
  try {
    const raw = localStorage.getItem(storageKey(businessId));
    if (raw) return JSON.parse(raw) as AppNotification[];
  } catch {}
  return [];
}

function persist(businessId: string, items: AppNotification[]) {
  // Keep at most 50 per business
  const trimmed = items.slice(0, 50);
  localStorage.setItem(storageKey(businessId), JSON.stringify(trimmed));
}

// ── Helpers ───────────────────────────────────────────────────────

function makeId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "Just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export { relativeTime };

// ── Provider ─────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { activeBusiness } = useBusiness();
  const businessId = activeBusiness?.id ?? null;
  const canManageJoinRequests = activeBusiness?.role === "owner";

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Pending errors to batch into a daily summary
  const pendingErrorsRef = useRef<string[]>([]);
  const errorDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pending successes to batch into a single "N invoices ready" notification
  const pendingSuccessRef = useRef<Array<{ id: string; supplier?: string; amount?: number | null }>>([]);
  const successDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active poll intervals/timeouts for watchForInvoices
  const pollIntervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);
  const pollTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Tracks invoice IDs already notified — prevents Realtime + poll firing twice
  const notifiedInvoiceIdsRef = useRef<Set<string>>(new Set());
  const notifiedJoinRequestIdsRef = useRef<Set<string>>(new Set());

  function clearAllPolls() {
    pollIntervalsRef.current.forEach(clearInterval);
    pollIntervalsRef.current = [];
    pollTimeoutsRef.current.forEach(clearTimeout);
    pollTimeoutsRef.current = [];
  }

  // ── Load from localStorage when business changes ───────────────
  useEffect(() => {
    if (!businessId) {
      setNotifications([]);
      clearAllPolls();
      notifiedInvoiceIdsRef.current.clear();
      notifiedJoinRequestIdsRef.current.clear();
      return;
    }
    const saved = load(businessId);
    setNotifications(saved);
    notifiedInvoiceIdsRef.current.clear();
    notifiedJoinRequestIdsRef.current = new Set(saved.map((item) => item.requestId).filter(Boolean) as string[]);
    return () => {
      clearAllPolls();
      notifiedInvoiceIdsRef.current.clear();
      notifiedJoinRequestIdsRef.current.clear();
    };
  }, [businessId]);

  // ── Persist whenever notifications change ─────────────────────
  useEffect(() => {
    if (!businessId) return;
    persist(businessId, notifications);
  }, [notifications, businessId]);

  // ── Core mutators ─────────────────────────────────────────────

  const pushNotification = useCallback(
    (notif: Omit<AppNotification, "id" | "timestamp" | "read">) => {
      const full: AppNotification = {
        ...notif,
        id: makeId(),
        timestamp: new Date().toISOString(),
        read: false,
      };
      setNotifications((prev) => [full, ...prev]);
    },
    [],
  );

  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // ── Error batching ────────────────────────────────────────────
  // Accumulate error invoice IDs then fire one summary after 8 s of silence

  const flushErrors = useCallback(() => {
    const ids = pendingErrorsRef.current;
    if (ids.length === 0) return;
    pendingErrorsRef.current = [];
    pushNotification({
      category: "error",
      title: `${ids.length} invoice${ids.length > 1 ? "s" : ""} flagged today`,
      body:
        ids.length === 1
          ? "An invoice couldn't be fully extracted. Review it in the Invoices tab."
          : `${ids.length} invoices had extraction issues on ${todayStr()}. Review them in the Invoices tab.`,
      errorCount: ids.length,
      errorDate: todayStr(),
    });
  }, [pushNotification]);

  const scheduleFlushErrors = useCallback(() => {
    if (errorDebounceRef.current) clearTimeout(errorDebounceRef.current);
    errorDebounceRef.current = setTimeout(flushErrors, 8_000);
  }, [flushErrors]);

  const flushSuccess = useCallback(() => {
    const items = pendingSuccessRef.current;
    if (items.length === 0) return;
    pendingSuccessRef.current = [];
    if (items.length === 1) {
      const { id, supplier, amount } = items[0];
      pushNotification({
        category: "invoice",
        title: supplier ? `Invoice from ${supplier} ready` : "Invoice ready",
        body: amount != null
          ? `Extracted successfully — Total: $${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
          : "Invoice extracted and ready to review.",
        invoiceId: id,
        supplier,
        amount,
      });
    } else {
      pushNotification({
        category: "invoice",
        title: `${items.length} invoices ready`,
        body: `${items.length} invoices were extracted and are ready to review.`,
      });
    }
  }, [pushNotification]);

  const scheduleFlushSuccess = useCallback(() => {
    if (successDebounceRef.current) clearTimeout(successDebounceRef.current);
    successDebounceRef.current = setTimeout(flushSuccess, 3_000);
  }, [flushSuccess]);

  // ── Supabase realtime subscription ───────────────────────────

  useEffect(() => {
    if (!businessId) return;

    // Tear down previous subscription
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(`notifs-${businessId}`)
      // ── New invoice uploaded ──────────────────────────────────
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "invoices",
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const rec = payload.new as Record<string, any>;

          if (rec.status === "duplicate") {
            const invoiceRef = rec.invoice_number ? `Invoice #${rec.invoice_number}` : "An invoice";
            pushNotification({
              category: "invoice",
              title: "Duplicate invoice detected",
              body: `${invoiceRef} already exists for this business. Open the Invoices page to delete it or overwrite the original.`,
              invoiceId: rec.id,
            });
            return;
          }

          if (rec.status === "complete") {
            if (notifiedInvoiceIdsRef.current.has(rec.id)) return;
            notifiedInvoiceIdsRef.current.add(rec.id);
            const supplier: string | undefined = rec.supplier_name ?? rec.supplier ?? undefined;
            const amount: number | null = rec.total != null ? Number(rec.total) : null;
            pendingSuccessRef.current.push({ id: rec.id, supplier, amount });
            scheduleFlushSuccess();
            return;
          }

          // For error statuses on insert, add to the debounced batch
          if (rec.status === "flagged" || rec.status === "extraction_failed") {
            pendingErrorsRef.current.push(rec.id);
            scheduleFlushErrors();
          }
          // status === "processing" / "pending" → wait for UPDATE to fire the real notification
        },
      )
      // ── Invoice update → complete, duplicate, flagged, extraction_failed ──
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "invoices",
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const rec = payload.new as Record<string, any>;
          const old = payload.old as Record<string, any>;

          if (rec.status === "complete" && old.status !== "complete") {
            if (notifiedInvoiceIdsRef.current.has(rec.id)) return;
            notifiedInvoiceIdsRef.current.add(rec.id);
            const supplier: string | undefined = rec.supplier_name ?? rec.supplier ?? undefined;
            const amount: number | null = rec.total != null ? Number(rec.total) : null;
            pendingSuccessRef.current.push({ id: rec.id, supplier, amount });
            scheduleFlushSuccess();
            return;
          }

          if (rec.status === "duplicate" && old.status !== "duplicate") {
            const invoiceRef = rec.invoice_number ? `Invoice #${rec.invoice_number}` : "An invoice";
            pushNotification({
              category: "error",
              title: "Possible duplicate invoice",
              body: `${invoiceRef} already exists for this business. Review it before approving.`,
              invoiceId: rec.id,
            });
            return;
          }

          const errorStatuses = ["flagged", "extraction_failed"];
          if (errorStatuses.includes(rec.status)) {
            pendingErrorsRef.current.push(rec.id);
            scheduleFlushErrors();
          }
        },
      )
      // ── generated_reports: notify when scheduled reports are ready ──
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "generated_reports",
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const rec = payload.new as Record<string, any>;
          const old = payload.old as Record<string, any>;
          if (rec.status === "ready" && old.status !== "ready") {
            const type = rec.report_type === "monthly" ? "monthly" : rec.report_type === "custom" ? "custom" : "weekly";
            const label = type === "monthly" ? "Monthly" : type === "custom" ? "Custom" : "Weekly";
            pushNotification({
              category: "report",
              title: `${label} report ready`,
              body: `Your ${type} report for ${rec.period_start} to ${rec.period_end} is ready to download.`,
              reportType: type,
            });
          }
        },
      )
      // ── join_requests: notify owners when someone requests access ──
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "join_requests",
          filter: `business_id=eq.${businessId}`,
        },
        async (payload) => {
          if (!canManageJoinRequests) return;

          const rec = payload.new as Record<string, any>;
          if (rec.status && rec.status !== "pending") return;
          if (notifiedJoinRequestIdsRef.current.has(rec.id)) return;
          notifiedJoinRequestIdsRef.current.add(rec.id);

          let requesterName: string | undefined;
          let requesterEmail: string | undefined;

          if (rec.user_id) {
            const { data } = await supabase
              .from("profiles")
              .select("name, email")
              .eq("id", rec.user_id)
              .maybeSingle();

            requesterName = (data?.name as string | undefined) || undefined;
            requesterEmail = (data?.email as string | undefined) || undefined;
          }

          const requester = requesterName || requesterEmail || "Someone";

          pushNotification({
            category: "team",
            title: "New join request",
            body: `${requester} requested access to ${activeBusiness?.name ?? "this business"}. Review it in Team Preferences.`,
            requestId: rec.id,
            requesterName,
            requesterEmail,
          });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      if (errorDebounceRef.current) clearTimeout(errorDebounceRef.current);
      if (successDebounceRef.current) clearTimeout(successDebounceRef.current);
    };
  }, [activeBusiness?.name, businessId, canManageJoinRequests, pushNotification, scheduleFlushErrors, scheduleFlushSuccess]);

  // ── Pending join request fallback ─────────────────────────────
  // Realtime only catches new INSERT events while this client is connected.
  // This sync backfills pending requests so owners still see them after sleep,
  // reloads, or missed realtime events.
  useEffect(() => {
    if (!businessId || !canManageJoinRequests) return;

    let cancelled = false;

    async function syncPendingJoinRequests() {
      const { data, error } = await supabase.rpc("get_pending_requests_for_owner");
      if (cancelled || error) return;

      ((data as Array<{
        request_id: string;
        user_name?: string | null;
        user_email?: string | null;
        business_id: string;
        business_name?: string | null;
      }>) ?? [])
        .filter((request) => request.business_id === businessId)
        .forEach((request) => {
          if (notifiedJoinRequestIdsRef.current.has(request.request_id)) return;
          notifiedJoinRequestIdsRef.current.add(request.request_id);

          const requester = request.user_name || request.user_email || "Someone";
          pushNotification({
            category: "team",
            title: "New join request",
            body: `${requester} requested access to ${request.business_name || activeBusiness?.name || "this business"}. Review it in Team Preferences.`,
            requestId: request.request_id,
            requesterName: request.user_name || undefined,
            requesterEmail: request.user_email || undefined,
          });
        });
    }

    syncPendingJoinRequests();
    const intervalId = setInterval(syncPendingJoinRequests, 60_000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [activeBusiness?.name, businessId, canManageJoinRequests, pushNotification]);

  // ── Polling fallback for invoice completion ───────────────────
  // Called by useScanInvoice after upload. Polls Supabase every 5 s until all
  // expected invoices leave processing status, then fires notifications.
  // This works even when the invoices table isn't in supabase_realtime.

  const watchForInvoices = useCallback(
    (since: string, expectedCount: number) => {
      if (!businessId || expectedCount <= 0) return;

      const seenIds = new Set<string>();
      let remaining = expectedCount;
      let intervalId: ReturnType<typeof setInterval>;
      const batchCompleted: Array<{ id: string; supplier?: string; amount?: number | null }> = [];

      const stopPoll = () => {
        clearInterval(intervalId);
        pollIntervalsRef.current = pollIntervalsRef.current.filter((id) => id !== intervalId);
      };

      const fireBatchSummary = () => {
        if (batchCompleted.length === 0) return;
        if (batchCompleted.length === 1) {
          const { id, supplier, amount } = batchCompleted[0];
          pushNotification({
            category: "invoice",
            title: supplier ? `Invoice from ${supplier} ready` : "Invoice ready",
            body: amount != null
              ? `Extracted successfully — Total: $${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : "Invoice extracted and ready to review.",
            invoiceId: id,
            supplier,
            amount,
          });
        } else {
          pushNotification({
            category: "invoice",
            title: `${batchCompleted.length} invoices ready`,
            body: `${batchCompleted.length} invoices were extracted and are ready to review.`,
          });
        }
      };

      const poll = async () => {
        if (remaining <= 0) { stopPoll(); return; }
        const { data } = await supabase
          .from("invoices")
          .select("id, status, supplier_name, total, invoice_number")
          .eq("business_id", businessId)
          .gte("created_at", since)
          .not("status", "in", "(processing,pending)");

        for (const inv of data ?? []) {
          if (seenIds.has(inv.id)) continue;
          seenIds.add(inv.id);
          remaining--;

          if (inv.status === "complete") {
            if (!notifiedInvoiceIdsRef.current.has(inv.id)) {
              notifiedInvoiceIdsRef.current.add(inv.id);
              const supplier: string | undefined = inv.supplier_name ?? undefined;
              const amount: number | null = inv.total != null ? Number(inv.total) : null;
              batchCompleted.push({ id: inv.id, supplier, amount });
            }
          } else if (inv.status === "duplicate") {
            const invoiceRef = inv.invoice_number ? `Invoice #${inv.invoice_number}` : "An invoice";
            pushNotification({
              category: "invoice",
              title: "Duplicate invoice detected",
              body: `${invoiceRef} already exists for this business. Open the Invoices page to delete it or overwrite the original.`,
              invoiceId: inv.id,
            });
          } else if (inv.status === "flagged" || inv.status === "extraction_failed") {
            pendingErrorsRef.current.push(inv.id);
            scheduleFlushErrors();
          }
        }

        if (remaining <= 0) {
          stopPoll();
          fireBatchSummary();
        }
      };

      poll();
      intervalId = setInterval(poll, 5_000);
      // Hard stop after 10 minutes regardless
      const timeoutId = setTimeout(stopPoll, 10 * 60 * 1000);

      pollIntervalsRef.current.push(intervalId);
      pollTimeoutsRef.current.push(timeoutId);
    },
    [businessId, pushNotification, scheduleFlushErrors],
  );

  // ── Expose addNotification for external use (reports, etc.) ──

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markRead,
        markAllRead,
        dismiss,
        clearAll,
        addNotification: pushNotification,
        watchForInvoices,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
